import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { useWorkspaceId } from '../lib/auth';
import type { ConnectionHealth, ConnectionProvider, ConnectionView } from '@app/shared';
import {
  Banner,
  Button,
  Cluster,
  ConfirmDialog,
  Heading,
  Icon,
  Icons,
  PageHeader,
  SelectMenu,
  type SelectMenuOption,
  Spinner,
  Stack,
  StatusBadge,
  Surface,
  Text,
  TextInput,
} from '../ui';
import { QueryState } from '../components/QueryState';
import { OAUTH_TOAST_KEY } from './OAuthCallbackPage';

/**
 * #126: connection-health → badge tone. `ok` is green; `error`/`expired` read red and
 * `degraded` amber so an unhealthy connection (e.g. a missing HubSpot scope) is visually
 * obvious. Unknown values fall back to neutral.
 */
const HEALTH_TONE: Record<ConnectionHealth, 'success' | 'danger' | 'warning' | 'neutral'> = {
  ok: 'success',
  error: 'danger',
  expired: 'danger',
  degraded: 'warning',
};

/**
 * Human, Title-Case display names for a connection's raw `provider` slug — the row and
 * the revoke dialog show these rather than the lowercase wire value (`google`,
 * `hubspot`, …). Unknown providers fall back to their raw slug so a new connector is
 * never rendered blank.
 */
const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  hubspot: 'HubSpot',
  dispatch: 'Dispatch',
  pitchreply: 'PitchReply',
  fake_email: 'Fake Email',
};

function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

/**
 * How a connector is connected — drives which inline control the "Add a connection"
 * dropdown reveals: an OAuth redirect (Google/HubSpot), a pasted PAT (Dispatch/
 * PitchReply), or the dev/E2E one-click double (fake_email).
 */
type ConnectorKind = 'oauth' | 'pat' | 'dev';

/**
 * The static connector catalogue that seeds the "Add a connection" dropdown. Each entry
 * is decorated at render with live `available` (from the provider's `…/status` query)
 * and `connected` (from `listConnections`) flags. `blurb` is the one-line hint under the
 * option label.
 */
interface ConnectorDef {
  id: ConnectionProvider;
  label: string;
  kind: ConnectorKind;
  blurb: string;
}

const CONNECTORS: ConnectorDef[] = [
  { id: 'google', label: 'Google', kind: 'oauth', blurb: 'Gmail, Calendar & Drive (OAuth)' },
  { id: 'hubspot', label: 'HubSpot', kind: 'oauth', blurb: 'CRM (OAuth)' },
  { id: 'dispatch', label: 'Dispatch', kind: 'pat', blurb: 'Newsletter (access token)' },
  { id: 'pitchreply', label: 'PitchReply', kind: 'pat', blurb: 'Replies (access token)' },
  { id: 'fake_email', label: 'Fake Email', kind: 'dev', blurb: 'Built-in test inbox (dev)' },
];

/** A connector decorated with its live availability + connected state for the dropdown. */
interface ConnectorEntry extends ConnectorDef {
  available: boolean;
  connected: boolean;
}

/**
 * Per-row "Test" outcome, keyed by connection id. A live probe is in-flight (`pending`),
 * returned a health verdict (`success` — `ok` plus the probed health), or threw
 * (`error` — a readable message). Keeping this per id means testing one row never
 * paints a result onto the others, and the feedback renders AT the row (the page-top
 * banner is easily scrolled off-screen).
 */
type TestState =
  | { status: 'pending' }
  | { status: 'success'; ok: boolean; health: ConnectionHealth }
  | { status: 'error'; message: string };

/**
 * Compact, inline Test outcome rendered next to a row's Test/Revoke buttons. Tone
 * carries the verdict: a spinner while probing, green "Healthy", red "Unhealthy — …"
 * or the error message.
 */
function TestResultChip({ state }: { state: TestState | undefined }) {
  if (!state) return null;
  if (state.status === 'pending') {
    return (
      <Cluster gap="2" wrap={false} data-testid="test-connection-result">
        <Spinner size="xs" tone="muted" label="Testing" />
        <Text as="span" size="sm" tone="muted">
          Testing…
        </Text>
      </Cluster>
    );
  }
  if (state.status === 'error') {
    // A provider error can be a long sentence (e.g. a missing-scope message). Cramming it
    // into a badge truncates/overflows awkwardly, so render it as wrapping text.
    return (
      <Text
        as="span"
        size="sm"
        tone="danger"
        data-testid="test-connection-result"
        style={{ maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}
      >
        {state.message}
      </Text>
    );
  }
  return (
    <StatusBadge
      tone={state.ok ? 'success' : 'danger'}
      label={state.ok ? 'Healthy' : `Unhealthy — ${state.health}`}
      data-testid="test-connection-result"
      style={{ textTransform: 'capitalize' }}
    />
  );
}

/**
 * Connections (§14). Two sections:
 *
 *  1. **Add a connection** — a single dropdown driven by a connector registry. Each
 *     provider's option reflects live `available` (from its `…/status` query) and
 *     `connected` (from `listConnections`) state; unavailable/already-connected options
 *     are disabled with a hint. Managed/auto-provisioned providers (e.g. a shared
 *     HubSpot Private App) are excluded — they aren't user-added. Selecting a provider
 *     reveals its connect control inline: an OAuth redirect (Google/HubSpot), a pasted
 *     PAT (Dispatch/PitchReply) or the one-click dev double (fake_email).
 *
 *  2. **Connected** — the `listConnections` rows with health + granted scopes. Every
 *     connection (managed included) can be **Tested**; only non-managed connections can
 *     be **Revoked** — a managed/shared one shows a quiet "Built-in" badge instead.
 *
 * The Google button initiates the real Google OAuth (offline access) flow wired in
 * WP-11/issue #53: it asks the API for an authorize URL and redirects the browser to
 * Google's consent screen (Gmail + Calendar + Drive in one consent). When the server has
 * no Google OAuth app configured the `google/status` endpoint reports
 * `configured: false`; the dropdown option is then disabled with a "not configured" hint.
 */
export function ConnectionsPage() {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const [banner, setBanner] = useState<string | null>(null);
  // The provider chosen in the "Add a connection" dropdown, whose connect control is
  // revealed inline below. '' = nothing selected (dropdown shows its placeholder).
  const [selectedProvider, setSelectedProvider] = useState<ConnectionProvider | ''>('');
  // §11.3: the pasted Dispatch PAT (never persisted client-side beyond submit).
  const [dispatchPat, setDispatchPat] = useState('');
  // §11.3 (v1.9.1): the pasted PitchReply PAT (never persisted client-side beyond submit).
  const [pitchreplyPat, setPitchreplyPat] = useState('');
  // Per-row Test outcome, keyed by connection id — see TestState. Drives the inline
  // spinner/verdict next to each row's Test button (the page-top banner scrolls away).
  const [testResults, setTestResults] = useState<Record<string, TestState>>({});
  // The connection awaiting a revoke confirmation. Revoke is irreversible and silently
  // breaks every agent bound to this connection, so it now goes through a confirm dialog
  // (mirroring the archive/full-autonomy confirms elsewhere) rather than firing on click.
  const [revokeTarget, setRevokeTarget] = useState<ConnectionView | null>(null);

  // One-shot toast handed over by the OAuth callback landing (success or error).
  useEffect(() => {
    let toast: string | null = null;
    try {
      toast = sessionStorage.getItem(OAUTH_TOAST_KEY);
      if (toast) sessionStorage.removeItem(OAUTH_TOAST_KEY);
    } catch {
      /* private mode — no toast to surface */
    }
    if (toast) {
      setBanner(toast);
      void queryClient.invalidateQueries({ queryKey: qk.connections(workspaceId) });
    }
  }, [queryClient, workspaceId]);

  const connections = useQuery({
    queryKey: qk.connections(workspaceId),
    queryFn: () => api.listConnections(workspaceId),
  });

  // Whether to offer the fake_email dev/E2E connector. Enabled for local dev + CI
  // (`NODE_ENV !== 'production'`), hidden in a deployed production build. Defaults to NOT
  // enabled while loading so production never briefly flashes it (default-safe).
  const fakeEmailStatus = useQuery({
    queryKey: qk.fakeEmailConnectStatus(workspaceId),
    queryFn: () => api.getFakeEmailConnectStatus(workspaceId),
  });
  const fakeEmailEnabled = fakeEmailStatus.data?.enabled ?? false;

  const googleStatus = useQuery({
    queryKey: qk.googleConnectStatus(workspaceId),
    queryFn: () => api.getGoogleConnectStatus(workspaceId),
  });
  const googleConfigured = googleStatus.data?.configured ?? false;

  const hubspotStatus = useQuery({
    queryKey: qk.hubspotConnectStatus(workspaceId),
    queryFn: () => api.getHubspotConnectStatus(workspaceId),
  });
  const hubspotConfigured = hubspotStatus.data?.configured ?? false;

  const dispatchStatus = useQuery({
    queryKey: qk.dispatchConnectStatus(workspaceId),
    queryFn: () => api.getDispatchConnectStatus(workspaceId),
  });
  const dispatchConfigured = dispatchStatus.data?.configured ?? false;

  const pitchreplyStatus = useQuery({
    queryKey: qk.pitchreplyConnectStatus(workspaceId),
    queryFn: () => api.getPitchreplyConnectStatus(workspaceId),
  });
  const pitchreplyConfigured = pitchreplyStatus.data?.configured ?? false;

  const connect = useMutation({
    mutationFn: () => api.connectFakeEmail(workspaceId),
    onSuccess: async () => {
      setSelectedProvider('');
      await queryClient.invalidateQueries({ queryKey: qk.connections(workspaceId) });
    },
    onError: (err) => setBanner(err instanceof ApiError ? err.message : 'Connect failed.'),
  });

  const connectGoogle = useMutation({
    mutationFn: () => api.startGoogleConnect(workspaceId),
    onSuccess: (res) => {
      // Redirect the browser to Google's consent screen (offline access).
      window.location.assign(res.authorizeUrl);
    },
    onError: (err) =>
      setBanner(
        err instanceof ApiError
          ? `Google connect unavailable: ${err.message}`
          : 'Google connect failed.',
      ),
  });

  const connectHubspot = useMutation({
    mutationFn: () => api.startHubspotConnect(workspaceId),
    onSuccess: (res) => {
      // Redirect the browser to HubSpot's consent screen.
      window.location.assign(res.authorizeUrl);
    },
    onError: (err) =>
      setBanner(
        err instanceof ApiError
          ? `HubSpot connect unavailable: ${err.message}`
          : 'HubSpot connect failed.',
      ),
  });

  const connectDispatch = useMutation({
    mutationFn: (pat: string) => api.connectDispatch(workspaceId, pat),
    onSuccess: async () => {
      setDispatchPat('');
      setSelectedProvider('');
      setBanner('Dispatch connected.');
      await queryClient.invalidateQueries({ queryKey: qk.connections(workspaceId) });
    },
    onError: (err) =>
      setBanner(
        err instanceof ApiError ? `Dispatch connect failed: ${err.message}` : 'Dispatch connect failed.',
      ),
  });

  const connectPitchreply = useMutation({
    mutationFn: (pat: string) => api.connectPitchreply(workspaceId, pat),
    onSuccess: async () => {
      setPitchreplyPat('');
      setSelectedProvider('');
      setBanner('PitchReply connected.');
      await queryClient.invalidateQueries({ queryKey: qk.connections(workspaceId) });
    },
    onError: (err) =>
      setBanner(
        err instanceof ApiError ? `PitchReply connect failed: ${err.message}` : 'PitchReply connect failed.',
      ),
  });

  const test = useMutation({
    mutationFn: (id: string) => api.testConnection(workspaceId, id),
    // Mark THIS row in-flight before the probe (per id, so other rows are untouched).
    onMutate: (id) => {
      setTestResults((prev) => ({ ...prev, [id]: { status: 'pending' } }));
    },
    onSuccess: async (res, id) => {
      setTestResults((prev) => ({
        ...prev,
        [id]: { status: 'success', ok: res.ok, health: res.health },
      }));
      // Keep the top banner as a secondary cue; tone follows the verdict.
      setBanner(`Test: ${res.ok ? 'healthy' : 'unhealthy'} (${res.health}).`);
      // Refresh the row's health badge with the freshly-probed value.
      await queryClient.invalidateQueries({ queryKey: qk.connections(workspaceId) });
    },
    // A network/500 failure must not be silent — surface it on the row.
    onError: (err, id) => {
      const message = err instanceof ApiError ? err.message : 'Test failed.';
      setTestResults((prev) => ({ ...prev, [id]: { status: 'error', message } }));
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeConnection(workspaceId, id),
    onSuccess: async () => {
      setRevokeTarget(null);
      await queryClient.invalidateQueries({ queryKey: qk.connections(workspaceId) });
    },
  });

  // Live availability per provider, from each connector's `…/status` query.
  const availableByProvider: Record<ConnectionProvider, boolean> = {
    google: googleConfigured,
    hubspot: hubspotConfigured,
    dispatch: dispatchConfigured,
    pitchreply: pitchreplyConfigured,
    fake_email: fakeEmailEnabled,
  };

  const rows = connections.data ?? [];
  // A provider whose (non-revoked) connection is MANAGED is auto-provisioned server-side
  // (e.g. a shared HubSpot Private App) — it is never a user-added option, so it is
  // excluded from the dropdown entirely (#118).
  const managedProviders = new Set(rows.filter((c) => c.managed).map((c) => c.provider));
  // Any non-revoked connection makes its provider "connected" — the option is then grayed.
  const connectedProviders = new Set(rows.map((c) => c.provider));

  // The dropdown registry: fake_email only appears when enabled; managed providers are
  // dropped; everything else is decorated with live available/connected flags.
  const registry: ConnectorEntry[] = CONNECTORS.filter(
    (c) => (c.id === 'fake_email' ? fakeEmailEnabled : true) && !managedProviders.has(c.id),
  ).map((c) => ({
    ...c,
    available: availableByProvider[c.id],
    connected: connectedProviders.has(c.id),
  }));

  // Registry → SelectMenu options. A connected or unavailable provider is disabled with a
  // hint explaining why; each option keeps the `add-connection-option-<id>` testid that
  // both the unit tests and the E2E flows drive.
  const addOptions: SelectMenuOption[] = registry.map((c) => ({
    value: c.id,
    label: c.label,
    hint: c.connected
      ? 'Already connected'
      : !c.available
        ? 'Not configured on this server'
        : c.blurb,
    disabled: c.connected || !c.available,
    'data-testid': `add-connection-option-${c.id}`,
  }));

  return (
    <Stack gap="8">
      <PageHeader title="Connections" description="Provider connections for this workspace" />

      {banner && (
        <Banner tone="info" onDismiss={() => setBanner(null)}>
          {banner}
        </Banner>
      )}

      {/* ── Section 1: Add a connection ─────────────────────────────────── */}
      <Stack as="section" gap="4">
        <Stack gap="1">
          <Heading level={2} size="lg">
            Add a connection
          </Heading>
          <Text size="sm" tone="secondary" style={{ maxWidth: 640 }}>
            Pick a provider to give your agents a mailbox, calendar, Drive or CRM to work with.
            Google and HubSpot connect via OAuth; Dispatch and PitchReply take an access token.
          </Text>
        </Stack>

        <SelectMenu
          label="Add a connection"
          placeholder="Add a connection"
          value={selectedProvider}
          options={addOptions}
          onChange={(value) => setSelectedProvider(value as ConnectionProvider)}
          minWidth="20rem"
          data-testid="add-connection-select"
        />

        {/* The selected connector's connect control, revealed inline. The connect-*
            testids are preserved so E2E can reach each control once revealed. */}
        {selectedProvider && (
          <Stack gap="3" data-testid="add-connection-panel">
            {selectedProvider === 'fake_email' && (
              <Cluster>
                <Button
                  onClick={() => connect.mutate()}
                  loading={connect.isPending}
                  data-testid="connect-fake-email"
                >
                  Connect Fake Email (dev)
                </Button>
              </Cluster>
            )}

            {selectedProvider === 'google' && (
              <Cluster>
                <Button
                  onClick={() => connectGoogle.mutate()}
                  disabled={!googleConfigured || googleStatus.isLoading}
                  loading={connectGoogle.isPending}
                  title={
                    googleConfigured
                      ? undefined
                      : 'Google connect is not configured on this server (set GOOGLE_CLIENT_ID/SECRET/REDIRECT_URI).'
                  }
                  data-testid="connect-google"
                >
                  Connect Google
                </Button>
              </Cluster>
            )}

            {selectedProvider === 'hubspot' && (
              <Cluster>
                <Button
                  onClick={() => connectHubspot.mutate()}
                  disabled={!hubspotConfigured || hubspotStatus.isLoading}
                  loading={connectHubspot.isPending}
                  title={
                    hubspotConfigured
                      ? undefined
                      : 'HubSpot connect is not configured on this server (set HUBSPOT_CLIENT_ID/SECRET/REDIRECT_URI).'
                  }
                  data-testid="connect-hubspot"
                >
                  Connect HubSpot
                </Button>
              </Cluster>
            )}

            {selectedProvider === 'dispatch' && (
              <Stack gap="2" style={{ maxWidth: 560 }}>
                <Text size="sm" tone="secondary">
                  Paste a Dispatch Personal Access Token (scopes: read/write/dispatch). It is
                  validated against the Dispatch server and stored encrypted — the token is never
                  shown again.
                </Text>
                <Cluster gap="3" align="center">
                  <TextInput
                    type="password"
                    size="sm"
                    aria-label="Dispatch token"
                    placeholder="Dispatch token"
                    value={dispatchPat}
                    onChange={(e) => setDispatchPat(e.target.value)}
                    disabled={!dispatchConfigured || connectDispatch.isPending}
                    data-testid="dispatch-pat-input"
                    style={{ maxWidth: 420, minWidth: 220, flexGrow: 1 }}
                  />
                  <Button
                    size="sm"
                    onClick={() => connectDispatch.mutate(dispatchPat.trim())}
                    disabled={
                      !dispatchConfigured ||
                      dispatchStatus.isLoading ||
                      dispatchPat.trim().length === 0
                    }
                    loading={connectDispatch.isPending}
                    title={
                      dispatchConfigured
                        ? undefined
                        : 'Dispatch connect is not available on this server (set AGENT_SECRET_ENCRYPTION_KEY).'
                    }
                    data-testid="connect-dispatch"
                  >
                    Connect
                  </Button>
                </Cluster>
                {connectDispatch.isError && (
                  <Text size="sm" tone="danger" data-testid="connect-dispatch-error">
                    {connectDispatch.error instanceof ApiError
                      ? connectDispatch.error.message
                      : 'Dispatch connect failed.'}
                  </Text>
                )}
              </Stack>
            )}

            {selectedProvider === 'pitchreply' && (
              <Stack gap="2" style={{ maxWidth: 560 }}>
                <Text size="sm" tone="secondary">
                  Paste a PitchReply Personal Access Token. It is validated against the PitchReply
                  server and stored encrypted — the token is never shown again.
                </Text>
                <Cluster gap="3" align="center">
                  <TextInput
                    type="password"
                    size="sm"
                    aria-label="PitchReply token"
                    placeholder="PitchReply token"
                    value={pitchreplyPat}
                    onChange={(e) => setPitchreplyPat(e.target.value)}
                    disabled={!pitchreplyConfigured || connectPitchreply.isPending}
                    data-testid="pitchreply-pat-input"
                    style={{ maxWidth: 420, minWidth: 220, flexGrow: 1 }}
                  />
                  <Button
                    size="sm"
                    onClick={() => connectPitchreply.mutate(pitchreplyPat.trim())}
                    disabled={
                      !pitchreplyConfigured ||
                      pitchreplyStatus.isLoading ||
                      pitchreplyPat.trim().length === 0
                    }
                    loading={connectPitchreply.isPending}
                    title={
                      pitchreplyConfigured
                        ? undefined
                        : 'PitchReply connect is not available on this server (set AGENT_SECRET_ENCRYPTION_KEY).'
                    }
                    data-testid="connect-pitchreply"
                  >
                    Connect
                  </Button>
                </Cluster>
                {connectPitchreply.isError && (
                  <Text size="sm" tone="danger" data-testid="connect-pitchreply-error">
                    {connectPitchreply.error instanceof ApiError
                      ? connectPitchreply.error.message
                      : 'PitchReply connect failed.'}
                  </Text>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>

      {/* ── Section 2: Connected ────────────────────────────────────────── */}
      <Stack as="section" gap="4">
        <Heading level={2} size="lg">
          Connected
        </Heading>

        <QueryState
          query={connections}
          empty={(d) => d.length === 0}
          emptyState={{
            icon: <Icon as={Icons.Link2} />,
            title: 'No connections yet',
            description:
              'Add a provider above to give your agents a mailbox, calendar, Drive, or CRM to work with.',
          }}
        >
          {(data) => (
            <Stack as="ul" gap="3" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {data.map((c) => (
                <Surface
                  as="li"
                  key={c.id}
                  bordered
                  radius="lg"
                  padding="4"
                  data-testid="connection-row"
                >
                  <Cluster justify="between" align="start" gap="4">
                    <Stack gap="2">
                      <Cluster gap="2" align="center">
                        <Heading level={3} size="sm" data-testid="connection-provider">
                          {providerLabel(c.provider)}
                        </Heading>
                        {/* #126: health badge — ok is green; error/expired read red,
                            degraded amber, so an unhealthy connection is obvious. */}
                        <StatusBadge
                          tone={HEALTH_TONE[c.health] ?? 'neutral'}
                          label={c.health}
                          data-testid="connection-health-chip"
                          style={{ textTransform: 'capitalize' }}
                        />
                        {/* #118: a managed (shared) connection is server-provisioned —
                            no per-user connect/revoke. Flag it clearly. */}
                        {c.managed && (
                          <StatusBadge
                            tone="success"
                            label="Connected (shared)"
                            data-testid="managed-badge"
                          />
                        )}
                      </Cluster>
                      <Text size="sm" tone="secondary">
                        {c.externalAccount}
                      </Text>
                      {/* #72: the connected account's display name (Google userinfo
                          `name`) is the identity the agent signs email as. Surface it
                          so the operator can confirm which name is in use. Absent when
                          no name / no profile scope was granted. */}
                      {c.displayName && (
                        <Text size="xs" tone="muted" data-testid="connection-display-name">
                          Signs as: {c.displayName}
                        </Text>
                      )}
                      {/* #126: an unhealthy connection surfaces its token-free reason
                          (e.g. a missing HubSpot scope) so the operator sees WHY —
                          applies to the managed "Connected (shared)" HubSpot row too. */}
                      {c.health !== 'ok' && c.healthReason && (
                        <Banner tone="danger" data-testid="connection-health-reason">
                          {c.healthReason}
                        </Banner>
                      )}
                      {/* §11.5 (v1.7): a Google account connected before Drive support
                          hasn't granted the Drive scope. Prompt a one-time reconnect —
                          the same Connect Google flow now requests Drive too. */}
                      {c.provider === 'google' && c.driveAuthorized === false && (
                        <Banner
                          tone="warning"
                          data-testid="drive-not-authorized"
                          action={
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => connectGoogle.mutate()}
                              loading={connectGoogle.isPending}
                              data-testid="reconnect-for-drive"
                            >
                              Reconnect
                            </Button>
                          }
                        >
                          Drive not authorized — reconnect Google to enable Drive tools.
                        </Banner>
                      )}
                      {c.scopes.length > 0 && (
                        <Cluster gap="2" wrap>
                          {c.scopes.map((s) => (
                            <StatusBadge key={s} tone="neutral" size="sm" label={s} />
                          ))}
                        </Cluster>
                      )}
                    </Stack>
                    {/* Test is available for EVERY connection, managed included.
                        Revoke is offered ONLY for non-managed connections — a
                        managed/shared credential is server-side (#118) and shows a
                        quiet "Built-in" badge instead. */}
                    <Cluster gap="2" align="center" wrap>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => test.mutate(c.id)}
                        loading={testResults[c.id]?.status === 'pending'}
                        data-testid="test-connection"
                      >
                        Test
                      </Button>
                      {/* Inline verdict right next to the button — the fix for
                          "Test shows no feedback": feedback appears AT the row. */}
                      <TestResultChip state={testResults[c.id]} />
                      {c.managed ? (
                        <StatusBadge
                          tone="neutral"
                          label="Built-in"
                          data-testid="managed-builtin-chip"
                        />
                      ) : (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setRevokeTarget(c)}
                          data-testid="revoke-connection"
                        >
                          Revoke
                        </Button>
                      )}
                    </Cluster>
                  </Cluster>
                </Surface>
              ))}
            </Stack>
          )}
        </QueryState>
      </Stack>

      {/* Revoke confirm. Revoking is irreversible and silently breaks every agent bound
          to this connection, so it requires an explicit confirm — mirroring the archive
          / full-autonomy confirms. Only Confirm calls revoke.mutate(). */}
      <ConfirmDialog
        open={revokeTarget != null}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => revokeTarget && revoke.mutate(revokeTarget.id)}
        title="Revoke this connection?"
        tone="danger"
        confirmLabel="Revoke connection"
        loading={revoke.isPending}
        confirmTestId="confirm-revoke"
        cancelTestId="revoke-cancel"
        data-testid="revoke-confirm-dialog"
        description={
          <Text tone="secondary" size="md">
            Revoking the{' '}
            <strong>{revokeTarget ? providerLabel(revokeTarget.provider) : ''}</strong> connection
            {revokeTarget?.externalAccount ? (
              <>
                {' '}
                (<strong>{revokeTarget.externalAccount}</strong>)
              </>
            ) : null}{' '}
            removes its access.{' '}
            <strong>Every agent that depends on this connection will lose access</strong> and its
            actions will start failing until it is reconnected. This cannot be undone.
          </Text>
        }
      />
    </Stack>
  );
}
